import { loadImage } from './photo';

export type ShareSize = '3x4' | '9x16';

const LOCKUP_W = 695;
const LOCKUP_H = 1037;
const CARD_BG_URL = '/img/card.png';
const FALLBACK_LOCALE = 'en';

// Photo box inside the 695×1037 lockup. Must stay in sync with the SCSS vars
// in IDCard.scss: ($lockup-x + $photo-x, $lockup-y + $photo-y, $photo-w, $photo-h).
// We re-stamp the captured portrait here as a belt-and-suspenders fallback for
// Safari, which silently drops `<img src="data:image/jpeg…">` from html-to-image's
// foreignObject capture.
const PHOTO_X = 87;
const PHOTO_Y = 265;
const PHOTO_W = 266;
const PHOTO_H = 322;

function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
): void {
  const imgAspect = img.naturalWidth / img.naturalHeight;
  const dstAspect = dw / dh;
  let sx = 0;
  let sy = 0;
  let sw = img.naturalWidth;
  let sh = img.naturalHeight;
  if (imgAspect > dstAspect) {
    sw = img.naturalHeight * dstAspect;
    sx = (img.naturalWidth - sw) / 2;
  } else {
    sh = img.naturalWidth / dstAspect;
    sy = (img.naturalHeight - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

interface ShareSpec {
  width: number;
  height: number;
  /** Where the lockup's top-left lands on the share BG, in image pixels. */
  lockupX: number;
  lockupY: number;
}

const SPECS: Record<ShareSize, ShareSpec> = {
  '3x4': { width: 1080, height: 1447, lockupX: 197, lockupY: 212 },
  '9x16': { width: 1080, height: 1920, lockupX: 197, lockupY: 449 },
};

const FILE_PREFIX: Record<ShareSize, 'reg' | 'tall'> = {
  '3x4': 'reg',
  '9x16': 'tall',
};

function bgUrl(size: ShareSize, locale: string): string {
  return `/img/share_${FILE_PREFIX[size]}_${locale}.jpg`;
}

async function loadBg(size: ShareSize, locale: string): Promise<HTMLImageElement> {
  try {
    return await loadImage(bgUrl(size, locale));
  } catch {
    return loadImage(bgUrl(size, FALLBACK_LOCALE));
  }
}

/** Composites the lockup PNG onto card.png and returns a 695×1037 PNG with
 * the original card.png shadow padding preserved as transparency.
 *
 * `photoDataUrl` is re-stamped on top of the lockup at the known photo-box
 * coordinates so Safari (which sometimes drops `<img>` data URLs from
 * html-to-image's foreignObject capture) still ends up with the portrait. */
export async function composeCard(
  lockupDataUrl: string,
  photoDataUrl?: string | null,
): Promise<string> {
  const [card, lockup, photo] = await Promise.all([
    loadImage(CARD_BG_URL),
    loadImage(lockupDataUrl),
    photoDataUrl ? loadImage(photoDataUrl) : Promise.resolve(null),
  ]);
  const canvas = document.createElement('canvas');
  canvas.width = LOCKUP_W;
  canvas.height = LOCKUP_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D context');
  ctx.drawImage(card, 0, 0, LOCKUP_W, LOCKUP_H);
  ctx.drawImage(lockup, 0, 0, LOCKUP_W, LOCKUP_H);
  if (photo) drawCover(ctx, photo, PHOTO_X, PHOTO_Y, PHOTO_W, PHOTO_H);
  return canvas.toDataURL('image/png');
}

/** Composites the lockup PNG onto the locale-matched share BG. `photoDataUrl`
 * is re-stamped at the photo-box position for the same Safari reason as
 * composeCard. */
export async function composeShare(
  size: ShareSize,
  lockupDataUrl: string,
  locale: string,
  photoDataUrl?: string | null,
): Promise<string> {
  const spec = SPECS[size];
  const [bg, lockup, photo] = await Promise.all([
    loadBg(size, locale),
    loadImage(lockupDataUrl),
    photoDataUrl ? loadImage(photoDataUrl) : Promise.resolve(null),
  ]);
  const canvas = document.createElement('canvas');
  canvas.width = spec.width;
  canvas.height = spec.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D context');
  ctx.drawImage(bg, 0, 0, spec.width, spec.height);
  ctx.drawImage(lockup, spec.lockupX, spec.lockupY, LOCKUP_W, LOCKUP_H);
  if (photo) {
    drawCover(ctx, photo, spec.lockupX + PHOTO_X, spec.lockupY + PHOTO_Y, PHOTO_W, PHOTO_H);
  }
  return canvas.toDataURL('image/png');
}
